

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import AssetActionMenu from "./AssetActionMenu";
import { AssetStatusFilterBar, AssetStatusLegend } from "../components/AssetStatusBadge";
import { useAssetStatusSummary } from "../hooks/useAssetStatusSummary";
import {
  countStatuses,
  markerStylesWithPending,
  type StatusFilter,
} from "../utilities/assetStatus";
import FloorplanResetDialog from "./FloorplanResetDialog";
import { resetFloorForNewPlan } from "./floorPlanReset";
import ImportMergeDialog from "./ImportMergeDialog";
import { useTranslation } from "react-i18next";
import { FloorplanEditor } from "../floorplanEditor/FloorplanEditor";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Check,
    ChevronDown,
    Copy,
    ExternalLink,
    Loader2,
    Pencil,
    Plus,
    Trash2,
} from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";
import { useAdministrationAPI } from "../AdministrationAPI";
import EntityDocumentsPanel from "../components/EntityDocumentsPanel";
import { assetsOnFloorFilter } from "../utilities/activeAssets";
import {
    Asset,
    Building,
    EntityQuery,
    Floor,
    FloorCategory,
    from,
    getConvexPolygonArea,
    is,
    Query,
    Space,
    Din277AreaTypeI18N,
    din277AreaTypesByLabel,
} from "@the-real-insight/tri-model";
import { optionSearchScore } from "./optionSearch";
import { userAPI } from "../UserAPI";
import { useDrag } from "react-dnd";
import { ColorSelector } from "../components/ColorSelector";
import Section from "../components/Section";
import { useLocation, useNavigate } from "react-router-dom";
import { useModalContext } from "../components/ModalProvider";
import { buildingFullAddressLine } from "../utilities/buildingFlatAddress";
import DIN276CategoryPicker from "../components/DIN276CategoryPicker";
import DocumentUploadArea from "../components/DocumentUploadArea";
import { din276CategoryMatchesFilter } from "../utilities/din276Category";
import { resolveDin276Style } from "../utilities/din276Style";
import MaterialSymbol from "../components/MaterialSymbol";

import { cn } from "@/lib/utils";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { DataTable } from "@/components/ui/data-table";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Rows per page in the rooms table. Also the divisor that turns a room's index
    into the page it sits on, so the two must stay together. */
const SPACES_PER_PAGE = 10;

/**
 * Die Zeilen der Tabellen dieser Seite sitzen enger.
 *
 * Eine Tabellenzelle trägt sonst 16 Bildpunkte oben wie unten. In einer Zeile, deren höchster
 * Inhalt ein Auswahlfeld von 36 Punkten ist, ist das mehr Polster als Inhalt: Die Zeile wird 68
 * Punkte hoch, und auf einen Bildschirm passen halb so viele Räume wie hineinpassten. Acht Punkte
 * lassen die Bedienelemente unverändert und geben die Hälfte davon zurück.
 */
const DENSE_ROWS = "[&>td]:py-2";

export const FLOOR_TAB_KEYS = [
  "general",
  "rooms",
  "equipment",
  "documents",
] as const;

const DEFAULT_FLOOR_TAB = FLOOR_TAB_KEYS[0];

function resolveFloorTab(tabFromUrl: string | null): string {
  return tabFromUrl && (FLOOR_TAB_KEYS as readonly string[]).includes(tabFromUrl)
    ? tabFromUrl
    : DEFAULT_FLOOR_TAB;
}

/** Closed value set for the context part — mirrors the server's list (Daten §1.2). */
const PERSON_EXPOSURES = [
  "publikumsbereich",
  "arbeitsplatzbereich",
  "technikbereich",
  "aussenbereich",
] as const;

/** Klickbarer Spaltenkopf — ersetzt die `sortable`-Köpfe der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

interface SearchSelectOption {
  key: string;
  text: string;
}

/**
 * Durchsuchbare Auswahl (früher Fluent-`ComboBox`): Popover + Command, weil die
 * Liste im Bestand eine Tippsuche hatte.
 */
function SearchSelect({
  id,
  placeholder,
  options,
  selectedKey,
  width,
  onSelect,
}: {
  id?: string;
  placeholder?: string;
  options: SearchSelectOption[];
  selectedKey?: string | null;
  width?: number;
  onSelect: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.key === selectedKey);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-label={placeholder}
        className="flex h-9 max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11"
        style={{ width: width ?? 240 }}
      >
        <span className={cn("line-clamp-1 text-left", !selected && "text-muted-foreground")}>
          {selected?.text ?? placeholder ?? "—"}
        </span>
        <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/*
          * Eigener Filter statt des Standards von cmdk.
          *
          * Dessen Bewertung nach Ähnlichkeit ist nicht vorhersagbar; hier gilt eine Regel, die sich
          * aussprechen lässt — gesucht wird als Teilzeichenkette, im Namen wie in der Normnummer,
          * und der Name wiegt schwerer. „bl" findet damit „NUF 4.1 - Blattgold".
          */}
        <Command filter={(value, search) => optionSearchScore(value, search)}>
          <CommandInput placeholder={placeholder ?? "Search…"} />
          <CommandList>
            <CommandEmpty>No options</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.key}
                  /* Nur die Beschriftung: Der Schlüssel „NUF4_1" steht nicht auf dem Schirm, und
                     eine Suche, die ihn mitnimmt, findet Einträge aus einem Grund, den niemand
                     sieht. Über die Nummer wird ohnehin gesucht — sie steht in der Beschriftung. */
                  value={option.text}
                  onSelect={() => {
                    onSelect(option.key);
                    setOpen(false);
                  }}
                >
                  <Check
                    aria-hidden="true"
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      option.key === selectedKey ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 whitespace-normal">{option.text}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

class Properties {
  floor: any;
  processError?: (error: any) => void;
}

interface SpaceEditorProperties {
  floor?: any;
  floorChanged: () => void;
  /** Zustimmung samt Zurücksetzen, bevor der Editor einen neuen Plan hochlädt (#735). */
  beforeImageReplaced?: () => Promise<boolean>;
}

function SpaceEditor(properties: SpaceEditorProperties) {
  const { t, i18n } = useTranslation();

  /** Die Sprache, in der die Nutzungsarten beschriftet werden — das Modell kennt nur diese zwei. */
  const labelLocale: 'de' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'de';
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]) as any;
  const [selectedSpaces, setSelectedSpaces] = useState<any[]>([]);
  const [bulkExposure, setBulkExposure] = useState<string | undefined>(undefined);
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [creating, setCreating] = useState(false) as any;
  const [selectedSpaceIndex, setSelectedSpaceIndex] = useState(-1);
  /** 1-based page of the rooms table, so a click on the plan can bring its row
      into view. Following the plan is only useful if the row is on screen. */
  const [spacesPage, setSpacesPage] = useState(1);
  /**
   * The room a click on the plan pointed at.
   *
   * Three states are in play here and they are deliberately separate:
   *  - `selectedSpaceIndex` — the editor draws this room's polygon for editing.
   *  - `selectedSpaces` — the ticked rows, i.e. what a bulk action applies to.
   *  - this one — where the reader is looking.
   *
   * Pointing at a room to see its data must neither start redrawing it nor join
   * it to a bulk edit. Both were tried and both were wrong: the first dropped
   * straight into the drawing mode, the second quietly extended a selection
   * someone had built up on purpose.
   */
  const [highlightedSpaceId, setHighlightedSpaceId] = useState<string | null>(null);
  const bulkExposureId = useId();

  /**
   * A room was clicked on the floorplan: mark its row and page the table to it.
   *
   * The plan is where a room is easy to point at, the table is where its data
   * is. Selecting in one and having to hunt in the other is what made the two
   * views feel unrelated.
   */
  const selectSpaceFromPlan = (index: number) => {
    setHighlightedSpaceId(spaces[index]?._id ?? null);
    setSpacesPage(Math.floor(index / SPACES_PER_PAGE) + 1);
  };
  const { createSpace, updateSpace, deleteSpace } = useAdministrationAPI();

  const loadSpaces = async () => {
    const spaces = await userAPI.query(
      from(Space.type).where(is("floor").equal(properties.floor._id)),
    );

    if (spaces !== null && spaces.length > 0) {
      setSpaces(spaces);
    } else {
      setSpaces([]);
    }
  };

  useEffect(() => {
    loadSpaces();
  }, [properties.floor]);

  /**
   * Der Raum, nach dessen Löschung gefragt wird — oder `null`.
   *
   * Ein Zustand für beide Wege: den Papierkorb in der Zeile und den am ausgewählten Raum im
   * Grundriss. Gelöscht wird ein Raum mit seinem Umriss und allem, was an ihm hängt, und das ist
   * nicht zurückzuholen; deshalb fragt beides dieselbe Frage.
   */
  const [spaceToDelete, setSpaceToDelete] = useState<any>(null);

  /** Löschen, nachdem zugestimmt wurde. Die Zeile verschwindet sofort, das Ablegen folgt. */
  const removeSpace = async (space: any) => {
    setSpaces((prev: any[]) => prev.filter((o: any) => o._id !== space._id));

    await deleteSpace(space);

    properties.floorChanged();
  };

  const toggleSpaceSelection = (space: any) =>
    setSelectedSpaces((prev) =>
      prev.some((s) => s._id === space._id)
        ? prev.filter((s) => s._id !== space._id)
        : [...prev, space],
    );

  // Latest mutable values for the memoised table columns. The columns array is
  // memoised so its reference stays stable across renders — otherwise the
  // DataTable rebuilds its TanStack columns every render and remounts each
  // cell, making the inline name/long-name inputs lose focus after every
  // keystroke. Cells read current state and API callbacks from this ref and
  // update via functional setState (keyed by _id) so they never need the
  // columns to be rebuilt.
  const liveRef = useRef<any>({});
  liveRef.current = {
    spaces,
    setSpaces,
    setSelectedSpaceIndex,
    updateSpace,
    deleteSpace,
    requestSpaceDelete: setSpaceToDelete,
    navigate,
    floorChanged: properties.floorChanged,
    selectedSpaces,
    toggleSpaceSelection,
    setSelectedSpaces,
  };

  /** Writes the chosen value to every selected room in one go. */
  const applyBulkExposure = async () => {
    if (!bulkExposure || selectedSpaces.length === 0) return;
    try {
      setApplyingBulk(true);
      for (const selected of selectedSpaces) {
        const current = spaces.find((s: any) => s._id === selected._id) || selected;
        await updateSpace(new Space({ ...current, personExposure: bulkExposure }));
      }
      await loadSpaces();
      setSelectedSpaces([]);
    } catch (error) {
      console.error(error);
    } finally {
      setApplyingBulk(false);
    }
  };

  const patchSpace = (id: string, patch: Record<string, unknown>) =>
    liveRef.current.setSpaces((prev: any[]) =>
      prev.map((s: any) => (s._id === id ? { ...s, ...patch } : s)),
    );

  const spacesColumns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "select",
        size: 40,
        enableSorting: false,
        header: () => {
          const r = liveRef.current;
          const all = (r.spaces as any[]) ?? [];
          const allSelected = all.length > 0 && all.every((s: any) => (r.selectedSpaces as any[]).some((x: any) => x._id === s._id));
          return (
            <Checkbox
              aria-label={t("context.bulk.applyToSelection")}
              checked={allSelected}
              onCheckedChange={(checked) =>
                r.setSelectedSpaces(checked === true ? [...all] : [])
              }
            />
          );
        },
        cell: ({ row }) => {
          const r = liveRef.current;
          const checked = (r.selectedSpaces as any[]).some((s: any) => s._id === row.original._id);
          return (
            <Checkbox
              aria-label={row.original.name || row.original._id}
              checked={checked}
              onCheckedChange={() => r.toggleSpaceSelection(row.original)}
            />
          );
        },
      },
      {
        id: "actions",
        header: "",
        size: 132,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("global.delete")}
                  // Nicht sofort löschen: Ein Raum trägt seinen Umriss und alles, was an ihm
                  // erfasst ist, und ein Fehlgriff neben dem Stift daneben wäre nicht zu
                  // widerrufen. Dieselbe Frage stellt der Papierkorb im Grundriss.
                  onClick={() => liveRef.current.requestSpaceDelete(row.original)}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("global.delete")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("global.edit")}
                  onClick={() => {
                    const r = liveRef.current;
                    r.setSelectedSpaceIndex(
                      r.spaces.findIndex((s: any) => s._id === row.original._id),
                    );
                  }}
                >
                  <Pencil aria-hidden="true" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("global.edit")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("spaceProperties.openSpacePage")}
                  onClick={() =>
                    liveRef.current.navigate(
                      `/entity?selectedEntityType=Space&selectedEntity=${row.original._id}`,
                    )
                  }
                >
                  <ExternalLink aria-hidden="true" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("spaceProperties.openSpacePage")}</TooltipContent>
            </Tooltip>
          </div>
        ),
      },
      {
        id: "name",
        header: "Name",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <Input
            className="h-8 w-[140px] max-w-full"
            aria-label="Name"
            value={row.original.name ?? ""}
            onChange={(event) => patchSpace(row.original._id, { name: event.target.value })}
            onBlur={() => {
              const r = liveRef.current;
              const latest =
                r.spaces.find((s: any) => s._id === row.original._id) || row.original;
              void r.updateSpace(latest);
            }}
          />
        ),
      },
      {
        id: "longName",
        header: "Long Name",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <Input
            className="h-8 w-[140px] max-w-full"
            aria-label="Long Name"
            value={row.original.longName ?? ""}
            onChange={(event) =>
              patchSpace(row.original._id, { longName: event.target.value })
            }
            onBlur={() => {
              const r = liveRef.current;
              const latest =
                r.spaces.find((s: any) => s._id === row.original._id) || row.original;
              void r.updateSpace(latest);
            }}
          />
        ),
      },
      {
        /*
         * Die Nutzungsart nach DIN 277 — an der Stelle, an der vorher die freie Raumtypliste stand.
         *
         * Die Liste kommt aus dem Modell, nicht aus den Sprachdateien hier: Eine Norm ist dieselbe
         * für jeden, der das Modell benutzt, und hier noch einmal gepflegt wäre sie zweimal geführt.
         *
         * `category` bleibt am Datensatz — die alten Werte aus Scans und Importen stehen dort und
         * gehen nicht verloren, sie werden hier bloß nicht mehr bearbeitet.
         */
        id: "din277Category",
        header: t("ui.din277AreaType"),
        size: 240,
        enableSorting: false,
        cell: ({ row }) => (
          <SearchSelect
            placeholder={t("ui.pleaseSelect")}
            width={230}
            options={din277AreaTypesByLabel(labelLocale).map((type) => ({
              key: type,
              text: Din277AreaTypeI18N[type][labelLocale],
            }))}
            selectedKey={row.original.din277Category}
            onSelect={async (key: string) => {
              const newSpace = { ...row.original, din277Category: key };
              await liveRef.current.updateSpace(newSpace);
              patchSpace(row.original._id, { din277Category: key });
            }}
          />
        ),
      },
      {
        id: "color",
        header: t("ui.color"),
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            {/* Kompakt: Die Beschriftung steht im Spaltenkopf, und ein Feld von
                60 Pixeln plus Wort darüber bestimmte bisher die Höhe jeder Zeile. */}
            <ColorSelector
              compact
              label={t("ui.color")}
              color={row.original.color}
              colorChanged={async (color: string) => {
                const newSpace = { ...row.original, color };
                await liveRef.current.updateSpace(newSpace);
                patchSpace(row.original._id, { color });
              }}
            ></ColorSelector>
          </div>
        ),
      },
      {
        id: "area",
        header: () => <div className="text-right">{t("ui.area")}</div>,
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.shape
              ? (getConvexPolygonArea(row.original.shape) / 1000000).toFixed(2)
              : "-"}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  // Grundriss (800px, fest) und Raumtabelle (~830px) stehen nur nebeneinander,
  // wenn beide wirklich nebeneinander passen; sonst bricht die Tabelle unter den
  // Grundriss um, statt auf ein paar hundert Pixel gequetscht zu werden. Vorher
  // erzwang `xl:flex-row` die Zeile schon ab 1280px — dort war die Tabelle dann
  // abgeschnitten (Issue #781).
  return (
    <TooltipProvider>
      <div className="flex min-w-0 flex-col gap-4">
        <div className="min-w-0 flex-1">
          <FloorplanEditor
                      beforeImageReplaced={properties.beforeImageReplaced}
            floor={properties.floor}
            floorChanged={properties.floorChanged}
            spaces={spaces}
            assets={[]}
            selectedSpaceIndex={selectedSpaceIndex}
            spaceClicked={selectSpaceFromPlan}
            spaceDeleteRequested={(space: any) => setSpaceToDelete(space)}
            selectedSpaceIndexChanged={(index: any) => setSelectedSpaceIndex(index)}
          ></FloorplanEditor>
        </div>
        <Section title={t("ui.rooms")} className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-h-[36px] items-center">
              {creating ? (
                <div className="flex items-center gap-2" aria-live="assertive">
                  <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t("ui.creating")}</span>
                </div>
              ) : (
                <Button
                  data-guide-id="entity.floor.createRoom"
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={async () => {
                    try {
                      setCreating(true);

                      await createSpace(
                        properties.floor,
                        new Space({
                          name: `${t("ui.room")} ${properties.floor.spaces.length + 1}`,
                          floor: properties.floor,
                        }),
                      );

                      await loadSpaces();

                      setCreating(false);
                      setSelectedSpaceIndex(properties.floor.spaces.length);

                      properties.floorChanged();
                    } catch (error) {
                      // TODO Process error
                      console.error(error);
                    }
                  }}
                >
                  <Plus aria-hidden="true" className="h-4 w-4" />
                  {t("ui.createRoom")}
                </Button>
              )}
            </div>
            {/* Bulk capture for the context part. Rooms are recorded in lists,
                not one at a time — without "apply to selection" the attribute
                simply does not get maintained across a few hundred rooms. */}
            <div className="flex min-w-0 flex-wrap items-end gap-4">
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={bulkExposureId}>{t("context.personExposure")}</Label>
                <Select
                  value={bulkExposure}
                  onValueChange={(value: string) => setBulkExposure(value)}
                >
                  <SelectTrigger id={bulkExposureId} className="w-[240px] max-w-full">
                    <SelectValue placeholder={t("context.bulk.chooseValue")} />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {PERSON_EXPOSURES.map((value) => (
                      <SelectItem key={value} value={value} className="whitespace-normal">
                        {t(`context.personExposureValue.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!bulkExposure || selectedSpaces.length === 0 || applyingBulk}
                onClick={applyBulkExposure}
              >
                {t("context.bulk.applyToSelection")}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t("context.bulk.selected", { count: selectedSpaces.length })}
              </span>
            </div>
            <div className="overflow-x-auto">
              <DataTable
                columns={spacesColumns}
                data={spaces}
                pageSize={SPACES_PER_PAGE}
                page={spacesPage}
                onPageChange={(page: number) => setSpacesPage(page)}
                emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
                // The room clicked on the plan, marked in the table. Same
                // highlight both ways round, so the two views read as one.
                rowClassName={(row: any) =>
                  cn(
                    DENSE_ROWS,
                    highlightedSpaceId && row?._id === highlightedSpaceId
                      ? "bg-accent font-semibold"
                      : undefined,
                  )
                }
              />
            </div>
          </div>
        </Section>

        {/* Die eine Rückfrage — für den Papierkorb in der Zeile wie für den im Grundriss. */}
        <AlertDialog
          open={!!spaceToDelete}
          onOpenChange={(open) => (open ? null : setSpaceToDelete(null))}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("ui.deleteRoomTitle", { name: spaceToDelete?.name ?? "" })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("ui.deleteRoomExplanation")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  const space = spaceToDelete;

                  setSpaceToDelete(null);

                  if (space) {
                    await removeSpace(space);
                  }
                }}
              >
                {t("global.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}

function AssetDnD(properties: any) {
  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: "AssetDnD",
    item: properties.asset,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return isDragging ? (
    <div
      ref={dragPreview}
      className="relative h-5 w-[100px] cursor-move border border-destructive p-1 opacity-0"
    >
      <span className="absolute -top-2.5 left-0 opacity-0">
        {properties.asset.name || properties.asset.id}
      </span>
    </div>
  ) : (
    <span ref={drag} className="font-bold">
      {properties.asset.name || properties.asset.id}
    </span>
  );
}

interface EquipmentEditorProperties {
  floor?: any;
  floorChanged?: (floor: any) => void;
  /** Zustimmung samt Zurücksetzen, bevor der Editor einen neuen Plan hochlädt (#735). */
  beforeImageReplaced?: () => Promise<boolean>;
}

function EquipmentEditor(properties: EquipmentEditorProperties) {
  const { t } = useTranslation();
  // Gerät, das auf den Plan gesetzt werden soll. Ziehen geht auf einem Tablet nicht (#735),
  // also: Gerät antippen, Stelle antippen.
  const [placementAsset, setPlacementAsset] = useState<any>(null);
  // Angetippte Anlage samt Tipp-Position: das Menü fragt, was mit ihr geschehen soll (#735).
  const [assetMenu, setAssetMenu] = useState<null | { asset: any; x: number; y: number }>(null);
  const navigate = useNavigate();
  const [metadata, setMetadata] = useState([]) as any;
  const [spaces, setSpaces] = useState([]) as any;
  const [assets, setAssets] = useState([]) as any;
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    undefined,
  );
  const { deleteAsset } = useAdministrationAPI();
  const din276FilterId = useId();

  // `metadata` is a code→category map; the DIN 276 filter helper wants the array form.
  const metadataList = useMemo(() => Object.values(metadata) as any[], [metadata]);
  const filteredAssets = useMemo(
    () =>
      (assets as any[])
        .filter((asset: any) =>
          din276CategoryMatchesFilter(asset.schemaCategory, categoryFilter, metadataList),
        )
        // Attach the DIN 276 category icon + colour so the floorplan markers use them.
        .map((asset: any) => {
          const category = metadata[asset.schemaCategory];
          const style = resolveDin276Style(asset.schemaCategory);
          return {
            ...asset,
            categoryColor: category?.color || style.color,
            categoryIcon: category?.icon || style.icon,
          };
        }),
    [assets, categoryFilter, metadataList, metadata],
  );
  // Status of every device placed on this floor, in one request (tri-server#1364): it styles
  // the markers, feeds the overview chips and the quick view in the action menu.
  const placedAssetIds = useMemo(
    () =>
      (assets as any[])
        .filter((asset: any) => asset?._id && asset.x !== undefined && asset.y !== undefined)
        .map((asset: any) => String(asset._id)),
    [assets],
  );
  const {
    summaries: statusSummaries,
    loading: statusLoading,
    pendingIds: statusPendingIds,
    error: statusError,
    reload: reloadStatus,
  } = useAssetStatusSummary(placedAssetIds);
  // The chips filter the plan: markers outside the chosen status are drawn faint, not removed —
  // the service round still sees where everything is.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const statusCounts = useMemo(
    () => countStatuses(placedAssetIds, statusSummaries),
    [placedAssetIds, statusSummaries],
  );
  const assetMarkerStyles = useMemo(
    () =>
      markerStylesWithPending(placedAssetIds, statusSummaries, statusFilter, statusPendingIds),
    [placedAssetIds, statusSummaries, statusFilter, statusPendingIds],
  );
  const { showDeleteDialog } = useModalContext() as any;
  const assetsColumns: ColumnDef<any, any>[] = [
    {
      id: "delete",
      header: "",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("ui.deleteDeviceEquipment")}
          onClick={() =>
            showDeleteDialog(
              t("ui.deleteDeviceEquipment"),
              row.original.id,
              async () => {
                await deleteAsset(row.original);

                refreshAssets();
              },
            )
          }
        >
          <Trash2 aria-hidden="true" className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "nameId",
      header: "Name/ID",
      size: 200,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <AssetDnD asset={row.original}></AssetDnD>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t("infoList.openLinked")}
            onClick={() =>
              navigate(
                `/entity?selectedEntityType=Asset&selectedEntity=${row.original._id}`,
              )
            }
          >
            <ExternalLink aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      id: "din276",
      header: ({ column }) => <SortableHeader column={column}>DIN 276 Type</SortableHeader>,
      size: 200,
      accessorFn: (asset: any) => asset.schemaCategory,
      cell: ({ row }) => {
        const category = metadata[row.original.schemaCategory];
        const style = resolveDin276Style(row.original.schemaCategory);
        const color = category?.color || style.color;
        const icon = category?.icon || style.icon;
        return (
          <div className="flex min-w-0 items-center gap-2">
            {icon ? (
              <MaterialSymbol name={icon} color={color} size={20} />
            ) : (
              /* Farbpunkt der Kategorie — die Farbe ist ein Datenwert und
                 bleibt deshalb wie im Bestand ein Inline-Wert. */
              <span
                className={color ? "inline-block h-3 w-3 shrink-0 rounded-sm" : "inline-block h-3 w-3 shrink-0 rounded-sm border"}
                style={color ? { backgroundColor: color } : undefined}
              />
            )}
            <div className="min-w-0">
              <div className="text-xs">{row.original.schemaCategory}</div>
              <div className="text-xs">
                {metadata[row.original.schemaCategory]
                  ? metadata[row.original.schemaCategory].name
                  : ""}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "floorAssignment",
      header: ({ column }) => <SortableHeader column={column}>Zuordnung Etage</SortableHeader>,
      size: 200,
      accessorFn: (asset: any) =>
        asset.floor
          ? asset.floor === properties.floor._id
            ? "Diese Etage"
            : "Andere Etage"
          : "Keine Etage",
      cell: ({ row }) => (
        <span>
          {row.original.floor
            ? row.original.floor === properties.floor._id
              ? "Diese Etage"
              : "Andere Etage"
            : "Keine Etage"}{" "}
          {row.original.x !== undefined && row.original.y !== undefined
            ? "(positioniert)"
            : "(nicht positioniert)"}
        </span>
      ),
    },
  ];

  const loadSpaces = async () => {
    setMetadata(
      (await userAPI.getDIN276Metadata()).reduce((map: any, category: any) => {
        map[category.code] = category;

        return map;
      }, {}),
    );

    const spaces = await userAPI.query(
      from(Space.type).where(is("floor").equal(properties.floor._id)),
    );

    setSpaces(spaces);
  };

  useEffect(() => {
    loadSpaces();
  }, []);

  const refreshAssets = async () => {
    if (!properties.floor) {
      return;
    }

    try {
      const result = await userAPI.query(
        new Query(
          new EntityQuery(
            Asset.type,
            [],
            undefined,
            assetsOnFloorFilter(properties.floor._id),
          ),
        ),
      );

      console.log(result);

      setAssets(result as Asset[]);
    } catch (error) {
      // TODO Process error

      console.error();
    }
  };

  useEffect(() => {
    const call = async () => {
      await refreshAssets();
    };

    call();
  }, [properties.floor]);

  // Gleiche Umbruchregel wie im Raum-Reiter, hier mit der schmaleren
  // Gerätetabelle (~680px) als Mindestbreite (Issue #781).
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="min-w-0 flex-1">
        {placedAssetIds.length > 0 ? (
          <AssetStatusFilterBar
            className="mb-2"
            counts={statusCounts}
            value={statusFilter}
            onChange={setStatusFilter}
            loading={statusLoading}
            error={!!statusError}
            onRetry={reloadStatus}
          />
        ) : null}
        <FloorplanEditor
                      beforeImageReplaced={properties.beforeImageReplaced}
          floor={properties.floor}
          floorChanged={properties.floorChanged}
          spaces={spaces}
          selectedSpaceIndex={-1}
          assets={filteredAssets}
          assetsChanged={() => refreshAssets()}
          placementAsset={placementAsset}
          placementFinished={() => setPlacementAsset(null)}
          placementSaved={(asset: any) =>
            toast.success(
              t("assetMenu.placed", { name: asset?.name || asset?.id || "" }),
              // Mittig über dem Plan, nicht in der Ecke: dort schaut gerade jeder hin.
              { position: "top-center" },
            )
          }
          assetClicked={(asset: any, point: { x: number; y: number }) =>
            setAssetMenu({ asset, x: point.x, y: point.y })
          }
          assetMarkerStyles={assetMarkerStyles}
          muteRooms
        ></FloorplanEditor>
        {placedAssetIds.length > 0 ? (
          <AssetStatusLegend className="mt-2" loading={statusLoading} error={!!statusError} />
        ) : null}
      </div>
      <Section title={t("ui.devicesAndEquipment")} className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="text-sm font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
            {t("ui.inThisBuilding")}
          </h3>
          <div className="flex min-w-0 flex-wrap items-end gap-2">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={din276FilterId}>{t("ui.din276Type")}</Label>
              <DIN276CategoryPicker
                value={categoryFilter}
                onChange={setCategoryFilter}
                includeEmptyOption
                emptyOptionText={t("ui.allTypes")}
                placeholder={t("ui.allTypes")}
                width={360}
              />
            </div>
            {categoryFilter ? (
              <span className="pb-2 text-xs text-muted-foreground">
                {filteredAssets.length}/{assets.length}
              </span>
            ) : (
              <></>
            )}
          </div>
          <div className="overflow-x-auto">
            <DataTable
              columns={assetsColumns}
              data={filteredAssets}
              pageSize={5}
              rowClassName={() => DENSE_ROWS}
              emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
            />
          </div>
          <h3 className="text-sm font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
            {t("floorProperties.inventoryHeading")}
          </h3>
          {/* Der Bestand wird zentral geführt (#785): die Liste unter Betrieb kennt jedes Gerät
              des Unternehmens — auch die, die in keinem Etagenplan stehen. */}
          <p className="max-w-3xl text-xs text-muted-foreground">
            {t("floorProperties.inventoryHint")}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => navigate("/settings?panel=assetInventory&unassigned=1")}
          >
            {t("floorProperties.inventoryOpen")}
          </Button>
        </div>
      </Section>
      {assetMenu ? (
        <AssetActionMenu
          asset={assetMenu.asset}
          x={assetMenu.x}
          y={assetMenu.y}
          onClose={() => setAssetMenu(null)}
          onMove={() => {
            setPlacementAsset(assetMenu.asset);
            setAssetMenu(null);
          }}
          onDetails={() => {
            const asset = assetMenu.asset;
            setAssetMenu(null);
            navigate(`/entity?selectedEntityType=Asset&selectedEntity=${asset._id}`);
          }}
          onDelete={() => {
            const asset = assetMenu.asset;
            setAssetMenu(null);
            // Löschen bleibt hinter der Rückfrage, die auch die Zeile benutzt.
            showDeleteDialog(t("ui.deleteDeviceEquipment"), asset.id, async () => {
              await deleteAsset(asset);
              refreshAssets();
            });
          }}
        />
      ) : null}
    </div>
  );
}

export default function FloorProperties(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [floor, setFloor] = useState(properties.floor) as any;
  const floorRef = useRef(properties.floor);
  const { updateFloor, mapFloorDwg, detectPlanType, analyzeFloorplanImage, deleteSpace, updateAsset } =
    useAdministrationAPI();
  const floorNameId = useId();
  const floorLevelId = useId();
  const floorCategoryId = useId();
  /**
   * The import dialog's switches.
   *
   * Merging is the default and overwriting has to be asked for: an import that
   * silently discarded curated data — categories, person exposure, hand-placed
   * assets — would be a bug, and the operator cannot get it back. The floorplan
   * image is its own switch, because replacing the picture and replacing the
   * rooms are separate decisions; a redrawn plan of the same rooms is the common
   * case.
   */
  const [importDialog, setImportDialog] = useState<null | { file: File; spaces: number; assets: number }>(null);
  // Ersetzen eines Rasterplans setzt die Etage zurück (#735) — vorher gefragt, mit Stückzahlen.
  const [resetDialog, setResetDialog] = useState<
    null | { file: File | null; spaces: any[]; assets: any[] }
  >(null);
  // Der Editor lädt selbst hoch und wartet nur auf ein Ja/Nein; sein Warten hängt an diesem
  // Resolver, den der Dialog beim Bestätigen oder Abbrechen auflöst.
  const resetDecision = useRef<null | ((proceed: boolean) => void)>(null);
  const [overwrite, setOverwrite] = useState({
    floors: false,
    spaces: false,
    doors: false,
    assets: false,
    floorplanImage: true,
  });
  const [dwgBusy, setDwgBusy] = useState(false);
  const [dwgStatus, setDwgStatus] = useState("");

  // Persist the selected tab in the URL (?tab=...) so it survives reloads
  // and deep links, without disturbing the existing selectedEntity* params.
  const [tab, setTab] = useState(() => resolveFloorTab(searchParams.get("tab")));

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const resolved = resolveFloorTab(tabFromUrl);
    if (tabFromUrl && tabFromUrl !== resolved) {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", resolved);
      window.history.replaceState(null, "", url.toString());
    }
    setTab((current) => (current === resolved ? current : resolved));
  }, [searchParams]);

  const setTabParam = (key: string) => {
    if (!key) return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", key);
    window.history.replaceState(null, "", url.toString());
    setTab(key);
  };

  useEffect(() => {
    floorRef.current = floor;
  }, [floor]);

  const changeFloor = async (changedFloor: any) => {
    await updateFloor(changedFloor);
    await refreshFloor();
  };

  const refreshFloor = async () => {
    const current = floorRef.current;
    if (!current?._id) {
      return;
    }

    const newFloor = await userAPI.queryOne(
      from(Floor.type).where(is("_id").equal(current._id)),
    );

    if (newFloor == null) {
      return;
    }

    const buildingId =
      (typeof newFloor.building === "string" ? newFloor.building : newFloor.building?._id) ??
      (typeof current.building === "string" ? current.building : current.building?._id);

    if (buildingId) {
      const building = await userAPI.queryOne(
        from(Building.type).where(is("_id").equal(buildingId)),
      );

      if (building !== null) {
        newFloor.building = building;
      }
    }

    setFloor(newFloor);
    floorRef.current = newFloor;
  };

  /**
   * Raster floorplans go to the analyzer, DWGs to the CAD import.
   *
   * The decision is made on the file itself — MIME type first, extension as the
   * fallback, since browsers report `application/octet-stream` for plenty of
   * files depending on the operating system. Both routes end in the same place:
   * a floorplan image on the floor and one `Space` per room.
   */
  const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".tif", ".tiff"];

  const isFloorplanImage = (file: File) => {
    if (file.type?.startsWith("image/")) {
      const isDwgMime = file.type === "image/vnd.dwg";
      if (isDwgMime) {
        return false;
      }
      return true;
    }
    const name = (file.name || "").toLowerCase();
    return IMAGE_EXTENSIONS.some((extension) => name.endsWith(extension));
  };

  const isPdfFile = (file: File) =>
    file.type === "application/pdf" || (file.name || "").toLowerCase().endsWith(".pdf");

  /**
   * Which pipeline a dropped plan belongs to. A raster image (scan/PNG/JPG) is always the raster
   * floorplan analysis (the server then picks scanned vs. escape-plan itself). A PDF is ambiguous —
   * a vector CAD plot needs the DWG/vector map, a scanned raster PDF needs the floorplan analysis,
   * and both are application/pdf — so ask the server to classify it. On any detection failure fall
   * back to the DWG/vector route (the prior behaviour). Anything else (.dwg) is vector.
   */
  const decidePlanRoute = async (file: File): Promise<"raster" | "vector"> => {
    if (isFloorplanImage(file)) {
      return "raster";
    }
    if (isPdfFile(file)) {
      try {
        const planType = await detectPlanType(file);
        if (planType === "scanned-architectural" || planType === "escape-plan") {
          return "raster";
        }
      } catch {
        // fall through to the DWG/vector route
      }
      return "vector";
    }
    return "vector";
  };

  const handleFloorplanFile = async (file: File | undefined | null) => {
    if (!file) {
      return;
    }
    const route = await decidePlanRoute(file);
    if (route === "raster") {
      void handleFloorplanImage(file);
    } else {
      void handleDwgFile(file);
    }
  };

  /**
   * Analysing an image replaces the floor's rooms, exactly as the DWG import
   * does — so it asks the same question first. Losing hand-drawn spaces to a
   * dropped file is not something to discover afterwards.
   */
  const handleFloorplanImage = async (file: File) => {
    if (!file || !floorRef.current?._id || dwgBusy) {
      return;
    }
    const targetFloor = floorRef.current;
    const floorId = targetFloor._id;
    const [existingSpaces, existingAssets] = await Promise.all([
      userAPI.query(from(Space.type).where(is("floor").equal(floorId))),
      userAPI.query(
        new Query(
          new EntityQuery(Asset.type, [], undefined, assetsOnFloorFilter(floorId)),
        ),
      ),
    ]);
    const spaceCount = existingSpaces?.length ?? 0;
    const assetCount = existingAssets?.length ?? 0;

    if (spaceCount > 0 || assetCount > 0) {
      // Ein neuer Plan macht Raumumrisse und Gerätepositionen des alten ungültig — sie stehen
      // in dessen Koordinaten. Deshalb ist die Frage hier nicht "kombinieren oder ersetzen",
      // sondern ob der Zurücksetzer laufen darf (#735).
      setResetDialog({ file, spaces: existingSpaces ?? [], assets: existingAssets ?? [] });
      return;
    }
    void runFloorplanAnalysis(targetFloor, file);
  };

  /**
   * Der Editor will den Plan ersetzen: fragen, und bei Zustimmung die Etage zurücksetzen,
   * BEVOR er hochlädt (#735). Liefert `false`, wenn abgebrochen wurde — dann bleibt alles.
   */
  const confirmAndResetForNewPlan = async (): Promise<boolean> => {
    const targetFloor = floorRef.current;

    if (!targetFloor?._id) {
      return true;
    }

    const floorId = targetFloor._id;
    const [existingSpaces, existingAssets] = await Promise.all([
      userAPI.query(from(Space.type).where(is("floor").equal(floorId))),
      userAPI.query(
        new Query(
          new EntityQuery(Asset.type, [], undefined, is("floor").equal(floorId)),
        ),
      ),
    ]);

    // Nichts da, was der neue Plan ungültig machen könnte — dann auch keine Rückfrage.
    if ((existingSpaces?.length ?? 0) === 0 && (existingAssets?.length ?? 0) === 0) {
      return true;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      resetDecision.current = resolve;
      setResetDialog({ file: null, spaces: existingSpaces ?? [], assets: existingAssets ?? [] });
    });

    if (!confirmed) {
      return false;
    }

    setDwgStatus(t("floorProperties.floorplanResetting"));
    const reset = await resetFloorForNewPlan(existingSpaces ?? [], existingAssets ?? [], {
      deleteSpace,
      updateAsset,
    });
    setDwgStatus(
      reset.failed > 0
        ? t("floorProperties.floorplanResetPartial", { count: reset.failed })
        : "",
    );
    await refreshFloor();

    return true;
  };

  /**
   * Etage zurücksetzen und den neuen Plan auswerten. Erst räumen, dann hochladen: die
   * Auswertung legt neue Räume an, und die dürfen nicht neben den alten stehen.
   */
  const replaceFloorplan = async (
    targetFloor: any,
    file: File,
    spaces: any[],
    assets: any[],
  ) => {
    setDwgBusy(true);
    setDwgStatus(t("floorProperties.floorplanResetting"));
    let reset;
    try {
      reset = await resetFloorForNewPlan(spaces, assets, { deleteSpace, updateAsset });
      // Der Maßstab gehört zum alten Bild. Stehen zu lassen hieße, Flächen und Abstände des
      // neuen Plans mit der Kalibrierung des vorherigen zu rechnen.
      await updateFloor(new Floor({ ...targetFloor, floorplanImageScale: null }));
    } catch (error: any) {
      setDwgStatus(`${t("floorProperties.floorplanResetFailed")}: ${error?.message ?? error}`);
      setDwgBusy(false);
      return;
    }
    setDwgBusy(false);

    if (reset.failed > 0) {
      setDwgStatus(t("floorProperties.floorplanResetPartial", { count: reset.failed }));
    }

    setOverwrite({
      floors: false,
      spaces: true,
      doors: true,
      // Geräte bleiben in der Hand des Portals — der Zurücksetzer hat nur ihre Position geleert.
      assets: false,
      floorplanImage: true,
    });
    await runFloorplanAnalysis({ ...targetFloor, floorplanImageScale: null }, file);
  };

  const runFloorplanAnalysis = async (targetFloor: any, file: File) => {
    if (!file || !targetFloor?._id || dwgBusy) {
      return;
    }
    setDwgBusy(true);
    setDwgStatus(t("floorProperties.floorplanAnalyzing"));
    try {
      const result = await analyzeFloorplanImage(new Floor(targetFloor), file, overwrite);
      const spaces = result?.spaces?.length ?? 0;
      const doors = result?.doors?.length ?? 0;
      setDwgStatus(t("floorProperties.floorplanDone", { spaces, doors }));
      await refreshFloor();
    } catch (error: any) {
      setDwgStatus(
        `${t("floorProperties.floorplanFailed")}: ${error?.message ?? error}`,
      );
    } finally {
      setDwgBusy(false);
    }
  };

  const handleDwgFile = async (file: File | undefined | null) => {
    if (!file || !floorRef.current?._id) {
      return;
    }
    if (dwgBusy) {
      return;
    }
    const targetFloor = floorRef.current;
    const floorId = targetFloor._id;
    const [existingSpaces, existingAssets] = await Promise.all([
      userAPI.query(from(Space.type).where(is("floor").equal(floorId))),
      userAPI.query(
        new Query(
          new EntityQuery(Asset.type, [], undefined, assetsOnFloorFilter(floorId)),
        ),
      ),
    ]);
    const spaceCount = existingSpaces?.length ?? 0;
    const assetCount = existingAssets?.length ?? 0;
    if (spaceCount > 0 || assetCount > 0) {
      // Same question as for a raster plan, so the same dialog: how should the
      // import be combined with what is already here?
      setOverwrite({
        floors: false,
        spaces: false,
        doors: false,
        assets: false,
        floorplanImage: true,
      });
      setImportDialog({ file, spaces: spaceCount, assets: assetCount });
      return;
    }
    void runDwgImport(targetFloor, file);
  };

  const runDwgImport = async (targetFloor: any, file: File) => {
    if (!file || !targetFloor?._id) {
      return;
    }
    if (dwgBusy) {
      return;
    }
    setDwgBusy(true);
    setDwgStatus(t("floorProperties.dwgStarting"));
    try {
      // The recognition/tri-server progress events carry English `message` text; translate them by
      // `phase` so the status line is localized (fall back to the raw message for unknown phases).
      const phaseKey: Record<string, string> = {
        intake: "progressReading",
        convert: "progressReading",
        extract: "progressExtracting",
        map: "progressMapping",
        adjudicate: "progressMapping",
        floorplan: "progressFloorplan",
        file_done: "progressFloorplan",
        persist: "progressSaving",
      };
      const done = await mapFloorDwg(
        new Floor(targetFloor),
        file,
        (event: any) => {
          if (event?.type === "progress") {
            const key = phaseKey[event.phase as string];
            setDwgStatus(
              key
                ? t(`floorProperties.${key}`)
                : event.message || t("floorProperties.dwgStarting"),
            );
          }
        },
        overwrite,
      );
      const summary = done?.floors?.[0];
      setDwgStatus(
        summary
          ? t("floorProperties.dwgDone", {
              spaces: summary.spaces ?? 0,
              assets: summary.assets ?? 0,
            })
          : t("floorProperties.dwgDoneGeneric"),
      );
      await refreshFloor();
    } catch (error: any) {
      setDwgStatus(`${t("floorProperties.dwgFailed")}: ${error?.message ?? error}`);
    } finally {
      setDwgBusy(false);
    }
  };

  useEffect(() => {
    const id = properties.floor?._id;
    if (!id) {
      return;
    }
    floorRef.current = properties.floor;
    setFloor(properties.floor);
    void refreshFloor();
  }, [properties.floor?._id]);

  return (
    <TooltipProvider>
      <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1">
            <p className="text-sm text-muted-foreground">
              Etage {floor.level !== undefined ? floor.level : ""}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Copy floor UUID"
                  onClick={() => navigator.clipboard.writeText(floor._id)}
                >
                  <Copy aria-hidden="true" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy floor UUID</TooltipContent>
            </Tooltip>
          </div>
          <h2 className="mt-0.5 break-words text-xl font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))] sm:text-2xl">
            {floor.name}
          </h2>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
            <span className="min-w-0 break-words text-sm">
              {floor.building ? buildingFullAddressLine(floor.building) : ""}
            </span>
            {floor.building ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label={t("infoList.openLinked")}
                onClick={() =>
                  navigate(
                    `/entity?selectedEntityType=Building&selectedEntity=${floor.building._id}`,
                  )
                }
              >
                <ExternalLink aria-hidden="true" className="h-4 w-4" />
              </Button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTabParam} className="min-w-0">
          <TabsList className="max-sm:[&_[role=tab]]:min-h-[44px]">
            <TabsTrigger value="general">Allgemein</TabsTrigger>
            <TabsTrigger value="rooms">{t("ui.rooms")}</TabsTrigger>
            <TabsTrigger value="equipment">{t("ui.devicesAndEquipment")}</TabsTrigger>
            <TabsTrigger value="documents">{t("entityDocuments.tab")}</TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="mt-4">
            <Section title="">
              <div className="flex min-w-0 flex-col gap-4">
                <div className="flex min-w-0 flex-wrap items-end gap-4">
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={floorNameId}>Name</Label>
                    <Input
                      id={floorNameId}
                      className="w-[250px] max-w-full"
                      value={floor.name ?? ""}
                      onChange={(event) => {
                        const next = { ...floorRef.current, name: event.target.value };
                        floorRef.current = next;
                        setFloor(next);
                      }}
                      onBlur={() => {
                        void changeFloor(new Floor(floorRef.current));
                      }}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={floorLevelId}>{t("ui.floorLevel")}</Label>
                    <Input
                      id={floorLevelId}
                      type="number"
                      className="w-[100px]"
                      min={-200}
                      max={200}
                      step={1}
                      value={floor.level ?? ""}
                      onChange={(event) => {
                        const next = {
                          ...floorRef.current,
                          level: Number(event.target.value),
                        };
                        floorRef.current = next;
                        setFloor(next);
                      }}
                      onBlur={() => {
                        void changeFloor(new Floor(floorRef.current));
                      }}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={floorCategoryId}>{t("ui.category")}</Label>
                    <SearchSelect
                      id={floorCategoryId}
                      placeholder={t("ui.pleaseSelect")}
                      options={Object.keys(FloorCategory)
                        .filter((item) => isNaN(Number(item)))
                        .map((key: string) => {
                          return { key, text: key };
                        })}
                      selectedKey={floor.category}
                      onSelect={async (key: string) => {
                        const newFloor = { ...floor, category: key };

                        await updateFloor(newFloor);

                        setFloor(newFloor);
                      }}
                    />
                  </div>
                </div>
                <FloorplanResetDialog
                  open={!!resetDialog}
                  spaces={resetDialog?.spaces.length ?? 0}
                  assets={
                    (resetDialog?.assets ?? []).filter(
                      (asset: any) => asset?.x != null || asset?.y != null || asset?.space != null,
                    ).length
                  }
                  onCancel={() => {
                    setResetDialog(null);
                    // Der Editor wartet auf die Antwort — ohne sie hinge sein Upload für immer.
                    resetDecision.current?.(false);
                    resetDecision.current = null;
                  }}
                  onConfirm={() => {
                    const pending = resetDialog;
                    setResetDialog(null);
                    if (resetDecision.current) {
                      // Der Editor lädt selbst hoch; er braucht nur das Ja.
                      resetDecision.current(true);
                      resetDecision.current = null;

                      return;
                    }
                    if (pending?.file && floorRef.current) {
                      void replaceFloorplan(
                        floorRef.current,
                        pending.file,
                        pending.spaces,
                        pending.assets,
                      );
                    }
                  }}
                />
                <ImportMergeDialog
                  open={!!importDialog}
                  spaces={importDialog?.spaces ?? 0}
                  assets={importDialog?.assets ?? 0}
                  overwrite={overwrite}
                  onChange={setOverwrite}
                  onCancel={() => setImportDialog(null)}
                  onConfirm={() => {
                    const pending = importDialog;
                    setImportDialog(null);
                    if (pending && floorRef.current) {
                      // The dialog is shared; the file decides the route, exactly
                      // as it does when nothing is on the floor yet (incl. asking the
                      // server to classify a PDF as raster scan vs. vector CAD).
                      const targetFloor = floorRef.current;
                      void (async () => {
                        const route = await decidePlanRoute(pending.file);
                        if (route === "raster") {
                          void runFloorplanAnalysis(targetFloor, pending.file);
                        } else {
                          void runDwgImport(targetFloor, pending.file);
                        }
                      })();
                    }
                  }}
                />
                <div className="min-w-0 max-w-[700px]">
                  <DocumentUploadArea
                    onFiles={(files) => {
                      const file = files?.[0];
                      if (file) void handleFloorplanFile(file);
                    }}
                    accept={{
                      "application/acad": [".dwg"],
                      "image/vnd.dwg": [".dwg"],
                      "application/dwg": [".dwg"],
                      // Vector CAD-plot PDFs go through the same map import as a DWG
                      // (the recognition server detects PDF by magic bytes and runs the
                      // PDF-ingest pipeline). isFloorplanImage() keeps them on the DWG route.
                      "application/pdf": [".pdf"],
                      // Also the fallback type for a .dwg/.pdf on many systems, so
                      // the extension decides — see `isFloorplanImage`.
                      "application/octet-stream": [".dwg", ".pdf", ".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".tif", ".tiff"],
                      "image/jpeg": [".jpg", ".jpeg"],
                      "image/png": [".png"],
                      "image/webp": [".webp"],
                      "image/gif": [".gif"],
                      "image/bmp": [".bmp"],
                      "image/tiff": [".tif", ".tiff"],
                    }}
                    hintKey="floorProperties.planDragHint"
                    activeHintKey="floorProperties.planDropHere"
                    multiple={false}
                    disabled={dwgBusy}
                  />
                  {dwgBusy || dwgStatus ? (
                    <div className="mt-2 flex flex-wrap items-center gap-4">
                      {dwgBusy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <></>}
                      {dwgStatus ? (
                        <span className="text-xs text-muted-foreground">
                          {dwgStatus}
                        </span>
                      ) : (
                        <></>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </Section>
          </TabsContent>
          <TabsContent value="rooms" className="mt-4">
            <SpaceEditor
              floor={floor}
              floorChanged={refreshFloor}
              beforeImageReplaced={confirmAndResetForNewPlan}
            ></SpaceEditor>
          </TabsContent>
          <TabsContent value="equipment" className="mt-4">
            <EquipmentEditor
              floor={floor}
              floorChanged={refreshFloor}
              beforeImageReplaced={confirmAndResetForNewPlan}
            ></EquipmentEditor>
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <EntityDocumentsPanel
              entityType="Floor"
              entity={floor}
              onChange={(updated: any) => {
                floorRef.current = updated;
                setFloor(updated);
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
